import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useContenidoAnalyticsOdf } from '../hooks/useAnalyticsOdf'
import type { FiltrosReporteOdf } from '../types'

const nf = (n: number) => n.toLocaleString('es-EC')

interface ContenidoTabProps {
  filtros: FiltrosReporteOdf
}

export function ContenidoTab({ filtros }: ContenidoTabProps) {
  const { data, isLoading, error } = useContenidoAnalyticsOdf(filtros)

  const filas = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.total - a.total),
    [data],
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <p className="text-destructive text-sm text-center max-w-md">
          No se pudo cargar el reporte de contenido. Verifique la configuración e intente de nuevo.
        </p>
      </div>
    )
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="text-sm font-medium">
          Top contenidos por accesos ({nf(filas.length)})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]">
                <TableHead>Título</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Leer</TableHead>
                <TableHead className="text-right">Descargar</TableHead>
                <TableHead className="text-right">Escuchar</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.length > 0 ? (
                filas.map((c) => (
                  <TableRow key={c.contenido_id}>
                    <TableCell className="font-medium max-w-[280px] truncate" title={c.titulo}>
                      {c.titulo}
                    </TableCell>
                    <TableCell className="capitalize text-muted-foreground">{c.tipo}</TableCell>
                    <TableCell className="text-right tabular-nums">{nf(c.leer)}</TableCell>
                    <TableCell className="text-right tabular-nums">{nf(c.descargar)}</TableCell>
                    <TableCell className="text-right tabular-nums">{nf(c.escuchar)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{nf(c.total)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                    Sin datos de contenido para los filtros seleccionados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
